<template>
  <a-modal
    v-model:visible="visible"
    :footer="false"
    :mask-closable="true"
    :closable="false"
    :width="'min(800px, 100%)'"
    :align-center="true"
    class="global-command-modal"
    @cancel="handleClose"
  >
    <template #title>
      <div class="modal-title">
        <span class="title-icon">✨</span>
        <span class="title-text">Hey {{ userName }}, 今天想洞察什么数据？</span>
      </div>
    </template>

    <div class="command-modal-content">
      <!-- 输入框区域 -->
      <div class="prompt-container">
        <div class="input-border-wrap"></div>
        <span class="search-icon">✨</span>
        <input
          ref="mainInputRef"
          v-model="inputValue"
          type="text"
          class="main-input"
          aria-label="输入自然语言查询"
          placeholder="输入自然语言，例如：帮我分析昨晚 iOS 渠道的付费异常..."
          @keyup.enter="handleSubmit"
          @keyup.esc="handleClose"
        />
        <button class="send-btn" aria-label="发送查询" :disabled="loading || !inputValue.trim()" @click="handleSubmit">
          ➜
        </button>
      </div>

      <!-- 推荐胶囊按钮 -->
      <div class="quick-actions">
        <div
          class="action-chip"
          role="button"
          tabindex="0"
          @click="handleQuickAction('分析昨晚流失原因')"
          @keydown.enter="handleQuickAction('分析昨晚流失原因')"
          @keydown.space.prevent="handleQuickAction('分析昨晚流失原因')"
        >
          🔍 分析昨晚流失原因
        </div>
        <div
          class="action-chip"
          role="button"
          tabindex="0"
          @click="handleQuickAction('生成 Q3 财务报表')"
          @keydown.enter="handleQuickAction('生成 Q3 财务报表')"
          @keydown.space.prevent="handleQuickAction('生成 Q3 财务报表')"
        >
          📊 生成 Q3 财务报表
        </div>
        <div
          class="action-chip"
          role="button"
          tabindex="0"
          @click="handleQuickAction('预测下周 LTV 趋势')"
          @keydown.enter="handleQuickAction('预测下周 LTV 趋势')"
          @keydown.space.prevent="handleQuickAction('预测下周 LTV 趋势')"
        >
          🔮 预测下周 LTV 趋势
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useCommandCenterStore } from '@/stores/commandCenter'

interface Props {
  modelValue: boolean
  userName?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  userName: '张三',
  loading: false
})

const emit = defineEmits<Emits>()
const router = useRouter()
const store = useCommandCenterStore()

const visible = ref(props.modelValue)
const inputValue = ref('')
const mainInputRef = ref<HTMLInputElement | null>(null)

// 同步外部 visible 状态
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    // 弹窗打开时聚焦输入框
    nextTick(() => {
      mainInputRef.value?.focus()
    })
  }
})

watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
  if (newVal) {
    // 弹窗打开时聚焦输入框
    nextTick(() => {
      mainInputRef.value?.focus()
    })
  } else {
    // 关闭时清空输入
    inputValue.value = ''
  }
})

// 提交消息
function handleSubmit() {
  if (!inputValue.value.trim() || props.loading) return
  
  // 先保存查询内容，避免被 handleClose 清空
  const query = inputValue.value.trim()
  
  // 设置 store 中的 query
  store.setQuery(query)
  store.submitQuery()
  
  // 跳转到 chat 分析页面
  router.push({
    name: 'chat',
    query: { q: query }
  })
  
  // 最后关闭弹窗
  handleClose()
}

// 快速操作 - 只填充输入框，不自动提交
function handleQuickAction(text: string) {
  inputValue.value = text
  // 聚焦到输入框，方便用户编辑或直接按回车提交
  nextTick(() => {
    mainInputRef.value?.focus()
  })
}

// 关闭弹窗
function handleClose() {
  visible.value = false
}
</script>

<style scoped lang="scss">
:deep(.arco-modal) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.arco-modal-header) {
  padding: 24px 32px 16px;
  border-bottom: none;
}

:deep(.arco-modal-body) {
  padding: 0 32px 32px;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.3px;
}

.title-icon {
  font-size: 24px;
}

.title-text {
  flex: 1;
}

.command-modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.prompt-container {
  position: relative;
  width: 100%;
}

.input-border-wrap {
  position: absolute;
  inset: -2px;
  border-radius: 36px;
  background: var(--c-text-1, #1F1E1D);
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.prompt-container:focus-within .input-border-wrap {
  opacity: 0.6;
}

.main-input {
  width: 100%;
  height: 68px;
  border-radius: 34px;
  border: 2px solid var(--color-border);
  background: var(--color-bg-base);
  padding: 0 80px 0 64px;
  font-size: 16px;
  color: var(--color-text-primary);
  box-shadow: var(--shadow-md);
  transition: border-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder {
    color: var(--color-text-disabled);
  }

  &:hover {
    border-color: var(--color-border-dark);
    box-shadow: var(--shadow-lg);
  }

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: var(--shadow-lg);
  }
}

.search-icon {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  z-index: 1;
  pointer-events: none;
}

.send-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: var(--c-text-1, #1F1E1D);
  border-radius: 50%;
  border: none;
  color: var(--color-bg-base);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-md);

  &:hover:not(:disabled) {
    background: var(--c-text-1, #1F1E1D);
    transform: translateY(-50%) scale(1.05);
    box-shadow: var(--shadow-lg);
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}

.quick-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-chip {
  background: var(--color-bg-base);
  border: 1.5px solid var(--color-border);
  padding: 10px 18px;
  border-radius: 24px;
  font-size: 14px;
  color: var(--color-text-tertiary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  min-height: 44px;
  box-shadow: var(--shadow-sm);

  &:hover {
    background: var(--color-bg-container);
    color: var(--color-primary);
    border-color: var(--color-primary-light);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}
</style>

<style lang="scss">
// 全局命令弹窗的遮罩样式
.global-command-modal {
  :deep(.arco-modal-mask) {
    background: var(--color-bg-mask);
  }
}
</style>

